import { useMutation, useQueryClient } from "@tanstack/react-query";
import md5 from "md5";
import { useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { twJoin } from "tailwind-merge";

import type { AvatarMimeType } from "@/api/bale.zod";

import { getGetProfileQueryKey, useUpdateAccount, useUploadAvatar, useGetProfileSuspense } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { IconButton } from "@/components/IconButton";
import { Input } from "@/components/Input";
import { RichTextarea } from "@/components/RichTextarea";
import { Subtitle } from "@/components/Subtitle";
import { useObjectUrl } from "@/hooks/useObjectUrl";
import { DEFAULT_PROFILE_AVATAR, profileAvatarUrl, profileDisplayName } from "@/model/profile";

export function EditingProfileCard({
  onStopEditing,
  accountId,
  className,
}: {
  onStopEditing?: () => void;
  accountId: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const [showingTurtle, setShowingTurtle] = useState(false);
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const profile = useGetProfileSuspense(accountId, {
    query: { select: (data) => data.data.profile },
  });
  const [profilePhotoUrl, setObject] = useObjectUrl(profileAvatarUrl(profile.data));

  const uploadPhoto = useUploadAvatar();

  const updateAccount = useUpdateAccount({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(getGetProfileQueryKey(data.data.profile.id), data);
        onStopEditing?.();
      },
    },
  });

  function previewProfilePhoto(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    setObject(file);
  }

  const submitForm = useMutation({
    mutationFn: async ({ form }: { form: FormData }) => {
      const profile_photo = form.get("profile_photo")! as File;
      const display_name = form.get("display_name")! as string;
      const real_name = form.get("real_name")! as string;
      const pronouns = form.get("pronouns")! as string;
      const biography = form.get("biography")! as string;
      let avatar_image_id = profile.data.avatar?.id ?? null;

      if (profile_photo?.size) {
        const content_md5 = Uint8Array.fromHex(md5(await profile_photo.bytes())).toBase64();
        const {
          data: { upload_url, image_id },
        } = await uploadPhoto.mutateAsync({
          data: {
            file_name: profile_photo.name,
            content_length: profile_photo.size,
            content_type: profile_photo.type as AvatarMimeType,
            content_md5,
          },
        });
        const response = await fetch(upload_url, {
          method: "PUT",
          headers: {
            "Content-Type": profile_photo.type,
            "Content-MD5": content_md5,
            "x-amz-acl": "public-read",
          },
          body: profile_photo,
        });
        if (!response.ok) {
          throw new Error("Failed to upload profile photo");
        }
        avatar_image_id = image_id;
      }
      return updateAccount.mutateAsync({
        data: { display_name, real_name, pronouns, biography, avatar_image_id },
      });
    },
  });

  return (
    <Card className={className}>
      <Form
        error={updateAccount.error}
        className="flex flex-row flex-wrap gap-8 p-4"
        onSubmit={(event) => {
          const form = new FormData(event.currentTarget);
          submitForm.mutate({ form });
        }}
      >
        <div className="flex flex-col gap-2">
          <div className="relative aspect-[88_/_80] h-auto max-h-80 w-88 max-w-full shrink-0">
            <div
              role="presentation"
              className={twJoin(
                "absolute aspect-square h-full overflow-hidden rounded-sm bg-water shadow-lg transition duration-125",
                // oxlint-disable-next-line no-negated-condition
                !showingTurtle ? "z-10" : "left-0 z-0 translate-x-16/80 scale-80 cursor-pointer",
              )}
              onClick={showingTurtle ? () => setShowingTurtle((showing) => !showing) : undefined}
            >
              <img src={profilePhotoUrl} className="size-full object-contain" alt={profileDisplayName(profile.data)} />
            </div>
            <div
              role="presentation"
              className={twJoin(
                "absolute aspect-square h-full overflow-hidden rounded-sm bg-water shadow-lg transition duration-125",
                showingTurtle ? "z-10" : "left-0 z-0 translate-x-16/80 scale-80 cursor-pointer",
              )}
              onClick={showingTurtle ? undefined : () => setShowingTurtle((showing) => !showing)}
            >
              <img src={DEFAULT_PROFILE_AVATAR} className="size-full object-contain" alt="Avatar turtle" />
            </div>
          </div>
          <div className="flex w-80 flex-col gap-2">
            <label className="flex h-[2.75rem] w-full cursor-pointer items-center justify-center rounded-full border border-divider bg-white px-4 font-bold hover:bg-[hsl(from_white_h_s_98)]">
              <input
                type="file"
                className="hidden"
                ref={fileInput}
                name="profile_photo"
                accept="image/png, image/jpeg, image/gif, image/webp, image/avif"
                onChange={previewProfilePhoto}
              />
              {t("upload-new-profile-photo")}
            </label>
            <Button type="button">{t("customize-avatar")}</Button>
          </div>
        </div>
        <div className="flex grow basis-1/3 flex-col gap-2">
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col">
              <Subtitle>{t("editing-profile")}</Subtitle>
            </div>
            <ActionBox>
              <IconButton type="button" name="x" onClick={() => onStopEditing?.()} />
              <IconButton type="submit" name="save" />
            </ActionBox>
          </div>
          <div className="flex flex-col gap-4">
            <FormField title={t("display-name")}>
              <Input
                name="display_name"
                type="text"
                defaultValue={profile.data.display_name ?? ""}
                placeholder={profile.data.id}
                disabled={updateAccount.isPending}
              />
            </FormField>
            <FormField title={t("real-name")}>
              <Input
                name="real_name"
                type="text"
                placeholder={t("real-name")}
                defaultValue={profile.data.real_name ?? ""}
                disabled={updateAccount.isPending}
              />
            </FormField>
            <FormField title={t("pronouns")}>
              <Input
                name="pronouns"
                type="text"
                defaultValue={profile.data.pronouns ?? ""}
                placeholder={t("pronouns-placeholder")}
                disabled={updateAccount.isPending}
              />
            </FormField>
            <FormField title={t("biography")}>
              <RichTextarea
                name="biography"
                rows={4}
                placeholder={t("biography-placeholder")}
                defaultValue={profile.data.biography?.raw ?? ""}
                disabled={updateAccount.isPending}
              />
            </FormField>
          </div>
        </div>
      </Form>
    </Card>
  );
}
